import { useFormikContext } from 'formik'
import * as React from 'react'
import { useTranslatable } from 'src/hooks/locale/utils'
import Select from 'src/views/components/Select'
import { SEMESTERS } from './constants'
import { type CourseEditFormValues, type GenCodeEntry } from './types'

const semesterOptions = SEMESTERS.map((sem) => ({
  label: String(sem),
  value: String(sem),
}))

export function GenCodesSection(): JSX.Element {
  const t = useTranslatable()
  const formik = useFormikContext<CourseEditFormValues>()

  const allCodes = React.useMemo(() => {
    const codes: string[] = []
    for (const code of formik.values.requiredCodes) {
      if (!codes.includes(code)) codes.push(code)
    }
    for (const code of formik.values.requiredElectiveCodes) {
      if (!codes.includes(code)) codes.push(code)
    }
    for (const code of formik.values.optionalCodes) {
      if (!codes.includes(code)) codes.push(code)
    }
    return codes
  }, [formik.values.requiredCodes, formik.values.requiredElectiveCodes, formik.values.optionalCodes])

  React.useEffect(() => {
    const existing = formik.values.genCodes
    const existingMap = new Map(existing.map((e) => [e.code, e.semesters]))

    const needsUpdate = allCodes.length !== existing.length || allCodes.some((code, i) => existing[i]?.code !== code)

    if (needsUpdate) {
      const updated: GenCodeEntry[] = allCodes.map((code) => ({
        code,
        semesters: existingMap.get(code) ?? [],
      }))
      void formik.setFieldValue('genCodes', updated)
    }
  }, [allCodes]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSemestersChange = React.useCallback(
    (index: number, selected: readonly { value: string }[] | null) => {
      const semesters = (selected ?? []).map((s) => Number.parseInt(s.value, 10)).filter((n) => Number.isFinite(n))
      const updated = formik.values.genCodes.map((entry, i) => (i === index ? { ...entry, semesters } : entry))
      void formik.setFieldValue('genCodes', updated)
    },
    [formik]
  )

  if (allCodes.length === 0) {
    return (
      <div className="rounded-lg border border-borderGrey bg-primaryWhite p-4">
        <div className="mb-3">
          <span className="font-bold text-primaryTextColor">
            {t(['პროგრამის კოდები და სემესტრები', 'Program Codes & Semesters'])}
          </span>
        </div>
        <p className="py-4 text-center text-caption text-captionColor">
          {t(['კოდები არ არის დამატებული', 'No codes added'])}
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-borderGrey bg-primaryWhite p-4">
      <div className="mb-3">
        <span className="font-bold text-primaryTextColor">
          {t(['პროგრამის კოდები და სემესტრები', 'Program Codes & Semesters'])}
        </span>
      </div>

      <div className="space-y-3">
        {formik.values.genCodes.map((entry, index) => (
          <GenCodeRow key={entry.code} entry={entry} index={index} onSemestersChange={handleSemestersChange} />
        ))}
      </div>
    </div>
  )
}

function GenCodeRow({
  entry,
  index,
  onSemestersChange,
}: {
  readonly entry: GenCodeEntry
  readonly index: number
  readonly onSemestersChange: (index: number, selected: readonly { value: string }[] | null) => void
}): JSX.Element {
  const t = useTranslatable()

  const semesterValue = React.useMemo(
    () => semesterOptions.filter((opt) => entry.semesters.includes(Number.parseInt(opt.value, 10))),
    [entry.semesters]
  )

  return (
    <div className="flex items-center gap-2">
      <div className="flex min-h-[38px] min-w-0 flex-1 items-center rounded-md border border-borderGrey bg-card px-3 py-1.5">
        <span className="text-sm font-medium text-primaryTextColor">{entry.code}</span>
      </div>
      <div className="min-w-0 flex-1">
        <Select
          isMulti
          options={semesterOptions}
          value={semesterValue}
          onChange={(selected) => onSemestersChange(index, selected as readonly { value: string }[] | null)}
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          placeholder={t(['სემესტრები', 'Semesters'])}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
        />
      </div>
    </div>
  )
}
